import { type ComputedRef, type WritableComputedRef, computed, watchEffect } from "vue";
import { useI18n } from "vue-i18n";
import { type EternalMod } from "../../../../graphql";
import { type EternalMods, useEternalStore } from "../../../../store/eternal";
import { useServiceStore } from "../../../../store/service";
import type { IUseMods } from "../index";

export interface UseEternalMods extends IUseMods<EternalMod> {
  eternalMods: ComputedRef<EternalMods>;
}

/**
 * Base Eternal/Curseforge mod interface. Each game has to implement the used settings (see LEAP game profile).
 * @param installedModIds The current installed mod IDs
 */
export function useEternalMods(installedModIds: WritableComputedRef<number[]>) {
  const eternal = useEternalStore();
  const service = useServiceStore();
  const { t } = useI18n();
  const storeTitle = computed(() => t("ngp/mods/dialog/link/curseforge"));

  // Load featured, popular, categories, etc.
  watchEffect(async () => {
    await eternal.loadEternalBootstrap({ profile: service.ctx.profile?.name });
  });

  watchEffect(async () => {
    await eternal.loadEternalInstalledMods({ modIds: installedModIds.value });
  });

  const eternalMods = computed(() => eternal.eternalMods);
  const installedMods = computed(() =>
    installedModIds.value
      .map((modId: number) => eternal.eternalMods.installed?.find((mod: EternalMod) => mod.id == modId))
      .filter((mod) => mod !== undefined)
      .map((mod) => mod!),
  );

  function addMod(mod: EternalMod) {
    if (installedModIds.value.includes(mod.id)) return;
    installedModIds.value.push(mod.id);
    installedModIds.value.sort();
    // Predictive UI - while we will trigger a reload anyway, this will make it look instant :)
    eternalMods.value.installed?.push(mod);
  }

  function removeMod(mod: EternalMod) {
    const installedIdIndex = installedModIds.value.indexOf(mod.id);
    if (installedIdIndex > -1) {
      installedModIds.value.splice(installedIdIndex, 1);
    }
  }

  function modTags(mod: EternalMod) {
    const result: Array<string> = [];
    for (const category of mod.categories ?? []) {
      // If mod is not a class itself, find parent category
      if (category?.classId) {
        const parentCategory = eternalMods.value.categories?.find((c) => c.id == category.classId);
        if (parentCategory && !result.includes(parentCategory.name)) {
          result.push(parentCategory.name);
        }
      }
      if (category && !result.includes(category.name)) {
        result.push(category.name);
      }
    }
    return result;
  }

  return { storeTitle, eternalMods, installedMods, addMod, removeMod, modTags };
}
